import { Box, Stack } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import type { StatusColor } from './StatusBlock';

export interface StackedSegment {
  readonly label: string;
  readonly count: number;
  readonly color: StatusColor;
}

export interface StackedStatusBarProps {
  readonly segments: readonly StackedSegment[];
}

function segmentColor(theme: Theme, color: StatusColor): string {
  switch (color) {
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.disabled;
  }
}

/**
 * Single horizontal bar split into proportional colored segments — the
 * VSO "case status" stacked bar. Theme-driven colors only.
 */
export function StackedStatusBar({ segments }: StackedStatusBarProps) {
  const total = segments.reduce((sum, s) => sum + s.count, 0);

  return (
    <Stack
      direction="row"
      sx={{
        height: 12,
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
        overflow: 'hidden',
        bgcolor: (theme) => theme.palette.action.hover,
      }}
    >
      {segments.map((s) => (
        <Box
          key={s.label}
          sx={{
            width: total > 0 ? `${(s.count / total) * 100}%` : '0%',
            bgcolor: (theme) => segmentColor(theme, s.color),
          }}
        />
      ))}
    </Stack>
  );
}
